import { useState, useEffect, useCallback, useMemo } from 'react';
import { getSessionTabId } from '../utils/tabId';
import {
   getActiveTabId,
   setActiveTab,
   updateHeartbeat,
   clearActiveTab,
   isHeartbeatStale,
   shouldTakePriority,
} from '../utils/tabManager';

const HEARTBEAT_INTERVAL = 1000; // 1 second

/**
 * Custom hook to manage tab state and coordination
 * Handles tab ID generation, active tab tracking, and heartbeat mechanism
 * New tabs automatically take priority over old tabs
 */
export const useTabManager = () => {
   // Generate stable tab ID and timestamp on mount
   const [tabId] = useState(() => getSessionTabId());
   const [tabTimestamp] = useState(() => Date.now());
   const [activeTabId, setActiveTabId] = useState(() => getActiveTabId());
   const [isTakingControl, setIsTakingControl] = useState(false);

   // Determine if this tab is currently active
   const isThisTabActive = useMemo(() => {
      // If no active tab exists, this tab should be active
      if (!activeTabId || activeTabId === 'null') return true;
      return activeTabId === tabId;
   }, [activeTabId, tabId]);

   // Take control of the active tab
   const takeControl = useCallback(() => {
      setActiveTab(tabId);
      setActiveTabId(tabId);
      setIsTakingControl(false);
   }, [tabId]);

   // Handle "Use Here" button click
   const handleTakeControl = useCallback(() => {
      if (isTakingControl) return;
      setIsTakingControl(true);
      takeControl();
   }, [isTakingControl, takeControl]);

   // Initialize - new tabs automatically take priority
   useEffect(() => {
      const currentActive = getActiveTabId();
      const isStale = isHeartbeatStale();

      // New tab should take control if:
      // 1. No active tab exists
      // 2. Active tab's heartbeat is stale
      // 3. This tab is newer (has priority)
      if (!currentActive || isStale || shouldTakePriority(tabTimestamp)) {
         // Defer to avoid synchronous update during render phase of other components
         setTimeout(() => takeControl(), 0);
      }
   }, [takeControl, tabTimestamp]);

   // Heartbeat mechanism - only active tab sends heartbeat
   useEffect(() => {
      if (!isThisTabActive) return;

      const interval = setInterval(() => {
         if (getActiveTabId() === tabId) {
            updateHeartbeat();
         }
      }, HEARTBEAT_INTERVAL);

      return () => clearInterval(interval);
   }, [isThisTabActive, tabId]);

   // Storage event listener - sync changes from other tabs
   useEffect(() => {
      const handleStorageChange = event => {
         if (event.key === 'activeTabId') {
            setActiveTabId(event.newValue);
         }
      };

      window.addEventListener('storage', handleStorageChange);
      return () => window.removeEventListener('storage', handleStorageChange);
   }, []);

   // Focus event - check if we should take control when tab gains focus
   useEffect(() => {
      const handleFocus = () => {
         const currentActive = getActiveTabId();
         const isStale = isHeartbeatStale();

         if (!currentActive || isStale) {
            takeControl();
         }
      };

      window.addEventListener('focus', handleFocus);
      return () => window.removeEventListener('focus', handleFocus);
   }, [takeControl]);

   // Cleanup on page unload
   useEffect(() => {
      const handleUnload = () => {
         if (getActiveTabId() === tabId) {
            clearActiveTab();
         }
      };

      window.addEventListener('beforeunload', handleUnload);
      return () => window.removeEventListener('beforeunload', handleUnload);
   }, [tabId]);

   // Safety timeout for loading state
   useEffect(() => {
      if (!isTakingControl) return;

      const timeout = setTimeout(() => {
         setIsTakingControl(false);
      }, 3000);

      return () => clearTimeout(timeout);
   }, [isTakingControl]);

   return {
      tabId,
      isThisTabActive,
      isTakingControl,
      handleTakeControl,
   };
};
